/**
 * What narrows one line item, on its own screen: the criteria X holds, a search over
 * X's places and languages to add one, a gender and an age bucket (since 2026-10-23),
 * an operating system, a device model and a place excluded (since 2026-10-29), and the
 * two switches — pause, and (since 2026-10-08) start.
 *
 * Three panels of the line item's screen (PR 3 of audits/2026-10-29/gui/plan.md), given to
 * the parent's grid as siblings: the line item with its switches in the header, its
 * criteria, and the forms that add one. They are one component because the start's gate
 * reads the criteria this file holds — split in two, the switch would ask a list it does
 * not own.
 *
 * 🔴 The screen's whole reason is the empty list. A line item with no criteria is
 * not half-configured — by X's documentation it reaches everyone — so an empty
 * answer is rendered as a warning rather than as an absence, and the warning
 * carries its attribution (`lib/targeting-copy.ts`).
 *
 * The decisions are in `lib/targeting-copy.ts` and in the worker; this file
 * renders them. The one thing it decides is the order of the two reads: criteria
 * first, because the warning depends on them.
 */
import { type FormEvent, useEffect, useState } from "react";
import {
	type Campaign,
	type CriterionType,
	type LineItem,
	type TargetingCriterion,
	type TargetingDevice,
	type TargetingLanguage,
	type TargetingLocation,
	type TargetingPlatform,
	UnauthorizedError,
	type XAccount,
	api,
} from "../lib/api.ts";
import { servableText } from "../lib/campaign-copy.ts";
import {
	ADD_CRITERION_PANEL,
	LINE_ITEM_PANEL,
	PAUSE,
	START,
	SWITCHES_FOLD,
	TARGETING_PANEL,
	underCampaign,
} from "../lib/line-item-screen-copy.ts";
import {
	type AskedStatus,
	type PendingStart,
	lineItemStatusOutcome,
	startAlreadyAsked,
	startGate,
} from "../lib/line-items.ts";
import {
	AGE_ALREADY,
	AGE_BUCKETS,
	AGE_ONLY_SEEN,
	CRITERION_EXISTS,
	CRITERION_UNKNOWN,
	DELETE_IS_ON_THE_CARD,
	GENDER_OPTIONS,
	KINDS_ADDED,
	NO_CRITERIA,
	ONLY_EXCLUSIONS,
	PLACE_IN_OR_OUT,
	PLATFORM_DEVICE_OVERLAP,
	READ_ONLY,
	START_CHECKS_THE_PARENT,
	START_IS_NOT_DELIVERY,
	START_IS_SAFE,
	STOP_IS_SAFE,
	ageLabel,
	ageTaken,
	criteriaEmptyText,
	criterionLabel,
	deviceLabel,
	languageLabel,
	loadedLabel,
	locationLabel,
	startWarning,
	switchText,
} from "../lib/targeting-copy.ts";
import { Badge, Button, Fold, InfoBanner, Input, Panel, Select, Table } from "../ui/index.ts";

interface Props {
	lineItem: LineItem;
	/** The line item's campaign, as the list read it: the header says its status and X's servable. */
	campaign: Campaign;
	account: XAccount;
	writable: boolean;
	/** Told to the parent after either switch, so the line-item row can be read again. */
	onSwitched?: () => void;
}

type Outcome =
	| { kind: "added"; id: string }
	| { kind: "exists" }
	| { kind: "unknown" }
	| { kind: "refused"; error: string };

export function Targeting({ lineItem, campaign, account, writable, onSwitched }: Props) {
	const [criteria, setCriteria] = useState<TargetingCriterion[] | null>(null);
	const [failed, setFailed] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [outcome, setOutcome] = useState<Outcome | null>(null);
	const [switched, setSwitched] = useState<string | null>(null);
	/**
	 * The warning a start has shown but not yet been confirmed through — **with the line
	 * item it belongs to**, which is what keeps it from outliving that line item (see
	 * `PendingStart`). The panel is also keyed on the line item by its parent, so this
	 * state cannot cross rows; the id here is the half that a test can measure.
	 */
	const [pending, setPending] = useState<PendingStart | null>(null);
	const [query, setQuery] = useState("");
	const [places, setPlaces] = useState<TargetingLocation[] | null>(null);
	const [searchFailed, setSearchFailed] = useState<string | null>(null);
	const [langQuery, setLangQuery] = useState("");
	const [languages, setLanguages] = useState<TargetingLanguage[] | null>(null);
	const [langFailed, setLangFailed] = useState<string | null>(null);
	const [gender, setGender] = useState("");
	const [age, setAge] = useState("");
	const [platforms, setPlatforms] = useState<TargetingPlatform[] | null>(null);
	const [platformsFailed, setPlatformsFailed] = useState<string | null>(null);
	const [deviceQuery, setDeviceQuery] = useState("");
	const [devices, setDevices] = useState<TargetingDevice[] | null>(null);
	const [devicesFailed, setDevicesFailed] = useState<string | null>(null);

	const readCriteria = () => {
		setFailed(null);
		api
			.targetingCriteria(account.xAccountId, lineItem.id)
			.then((page) => setCriteria(page.criteria))
			.catch((e) => {
				if (e instanceof UnauthorizedError) return;
				// 🔴 Not an empty list: a read that failed says nothing about the targeting,
				// and showing "reaches everyone" on a failure would be inventing an answer.
				setCriteria(null);
				setFailed(e instanceof Error ? e.message : String(e));
			});
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: the two ids ARE the subject — this effect must re-run when the line item or the account changes, and `readCriteria` closes over exactly those two
	useEffect(() => {
		setCriteria(null);
		setOutcome(null);
		setSwitched(null);
		setPlaces(null);
		setQuery("");
		setLanguages(null);
		setLangQuery("");
		setGender("");
		setAge("");
		setPlatforms(null);
		setDevices(null);
		setDeviceQuery("");
		readCriteria();
	}, [lineItem.id, account.xAccountId]);

	async function search(e: FormEvent) {
		e.preventDefault();
		if (!query.trim() || busy) return;
		setBusy(true);
		setSearchFailed(null);
		try {
			const page = await api.targetingLocations(query.trim());
			setPlaces(page.locations);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setPlaces(null);
			setSearchFailed(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	async function searchLanguages(e: FormEvent) {
		e.preventDefault();
		if (!langQuery.trim() || busy) return;
		setBusy(true);
		setLangFailed(null);
		try {
			const page = await api.targetingLanguages(langQuery.trim());
			setLanguages(page.languages);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setLanguages(null);
			setLangFailed(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	/** X's operating systems, read when asked for: one page, as the wizard reads them. */
	async function readPlatforms() {
		if (busy) return;
		setBusy(true);
		setPlatformsFailed(null);
		try {
			setPlatforms((await api.targetingPlatforms()).platforms);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setPlatforms(null);
			setPlatformsFailed(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	async function searchDevices(e: FormEvent) {
		e.preventDefault();
		if (!deviceQuery.trim() || busy) return;
		setBusy(true);
		setDevicesFailed(null);
		try {
			setDevices((await api.targetingDevices(deviceQuery.trim())).devices);
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setDevices(null);
			setDevicesFailed(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	/**
	 * One criterion; a place goes without a type, as it always has. `NE` is a place excluded
	 * (since 2026-10-29) and is the only operator ever named here — every other add is EQ.
	 */
	async function add(targetingType: CriterionType, targetingValue: string, exclude = false) {
		if (!writable || busy || targetingValue === "") return;
		setBusy(true);
		try {
			const { status, body } = await api.createTargetingCriterion({
				account: account.xAccountId,
				lineItemId: lineItem.id,
				...(targetingType === "LOCATION" ? {} : { targetingType }),
				...(exclude && targetingType === "LOCATION" ? { operatorType: "NE" as const } : {}),
				targetingValue,
			});
			const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
			if (b.created === true) setOutcome({ kind: "exists" });
			else if (b.created === "unknown") setOutcome({ kind: "unknown" });
			else if (status === 201 && typeof b.id === "string") setOutcome({ kind: "added", id: b.id });
			else
				setOutcome({
					kind: "refused",
					error: typeof b.error === "string" ? b.error : `HTTP ${status}`,
				});
			// Read again whatever happened: after `unknown` the list is the only thing that
			// can say whether the narrowing exists, and after a 201 it is the answer.
			readCriteria();
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			// The request may have reached X, so it is `unknown` and never a refusal.
			setOutcome({ kind: "unknown" });
			readCriteria();
		} finally {
			setBusy(false);
		}
	}

	/**
	 * One switch, either way. The sentence is not built here: `lineItemStatusOutcome`
	 * reads the answer and `switchText` words it, so the consequence an operator reads
	 * has tests on it (`lib/line-items.ts`, `lib/targeting-copy.ts`) instead of living
	 * in a component nothing runs. What this function decides is only the reload — the
	 * parent re-reads the row after either direction, because the badge beside the
	 * button is this screen's answer and the row above it is the list's.
	 */
	async function switchTo(asked: AskedStatus) {
		if (!writable || busy) return;
		/*
		 * 🔴 The start's one stop on the way: when nothing narrows this line item, the
		 * first press names what it would cost and sends nothing. The second press sends.
		 *
		 * It is a confirmation and not a refusal because starting delivery is the owner's
		 * decision; the console's job is to put the price in front of it (the owner, via
		 * e1, 2026-10-08). And it is here rather than in the worker on purpose: the
		 * route's one condition is the parent campaign, by the same decision, and X
		 * serves an untargeted line item perfectly happily — so a refusal in the route
		 * would be this console inventing a rule X does not have.
		 *
		 * 🔴 A stop is never gated. It takes money off; nothing may stand between an
		 * operator and it.
		 */
		if (asked === "ACTIVE") {
			const warning = startWarning(startGate(criteria));
			if (warning !== null && !startAlreadyAsked(lineItem.id, warning, pending)) {
				setPending({ lineItemId: lineItem.id, text: warning });
				setSwitched(null);
				return;
			}
		}
		setPending(null);
		setBusy(true);
		try {
			const { status, body } = await api.setLineItemStatus(lineItem.id, account.xAccountId, asked);
			setSwitched(switchText(lineItemStatusOutcome(asked, status, body)));
			onSwitched?.();
		} catch (err) {
			if (err instanceof UnauthorizedError) return;
			setSwitched(err instanceof Error ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	}

	const untargeted = criteria !== null && criteria.length === 0;
	// Not empty, and still not narrowed to anywhere it targets: the start's own gate (targeting v2).
	const onlyExclusions = startGate(criteria) === "excludes-only";
	const ageClosed = ageTaken(criteria);

	return (
		<>
			{/*
			 * The line item's header panel: what it is, under which campaign, and the two
			 * switches. Here rather than under the forms (where they stood until PR 3) because
			 * a switch is the act this screen exists for, and a button below four forms was the
			 * one an operator had to scroll for (gui-spec §4).
			 */}
			<Panel
				className="@3xl:col-span-3"
				title={LINE_ITEM_PANEL}
				aside={
					<>
						{lineItem.name && (
							<span className="text-sm font-medium text-content">{lineItem.name}</span>
						)}
						<span className="font-mono text-xs text-muted">{lineItem.id}</span>
						<Badge variant={lineItem.entityStatus === "ACTIVE" ? "info" : "default"}>
							{lineItem.entityStatus}
						</Badge>
					</>
				}
				actions={
					writable ? (
						<>
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={busy}
								onClick={() => void switchTo("PAUSED")}
							>
								{PAUSE}
							</Button>
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={busy}
								onClick={() => void switchTo("ACTIVE")}
							>
								{START}
							</Button>
						</>
					) : undefined
				}
			>
				<p className="flex flex-wrap items-center gap-2 text-sm text-muted">
					<span>{underCampaign(campaign.id)}</span>
					<Badge variant={campaign.entityStatus === "ACTIVE" ? "info" : "default"}>
						{campaign.entityStatus}
					</Badge>
					<span>{servableText(campaign.servable, campaign.reasonsNotServable)}</span>
				</p>
				{switched && (
					<div>
						<Badge variant="default">{switched}</Badge>
					</div>
				)}
				{pending && (
					<InfoBanner variant="warning" title="Start this line item as it is?">
						{pending.text}
					</InfoBanner>
				)}
				{!writable && <p className="text-sm text-muted">{READ_ONLY}</p>}
				{writable && (
					<>
						{/* The sentence the start needs most, and never folded: what ACTIVE is not. */}
						<p className="text-sm text-muted">{START_IS_NOT_DELIVERY}</p>
						<Fold summary={SWITCHES_FOLD}>
							<p>{STOP_IS_SAFE}</p>
							<p className="mt-1">{START_IS_SAFE}</p>
							<p className="mt-1">{START_CHECKS_THE_PARENT}</p>
						</Fold>
					</>
				)}
			</Panel>

			<Panel
				title={TARGETING_PANEL}
				footer={
					criteria !== null && criteria.length > 0 ? (
						<span>{loadedLabel(criteria.length)}</span>
					) : undefined
				}
			>
				{failed && (
					<InfoBanner variant="danger" title="This line item's targeting could not be read.">
						{failed}
					</InfoBanner>
				)}
				{untargeted && (
					<InfoBanner variant="warning" title={NO_CRITERIA.title}>
						{NO_CRITERIA.body}
					</InfoBanner>
				)}
				{onlyExclusions && (
					<InfoBanner variant="warning" title={ONLY_EXCLUSIONS.title}>
						{ONLY_EXCLUSIONS.body}
					</InfoBanner>
				)}
				{outcome?.kind === "exists" && (
					<InfoBanner variant="warning" title={CRITERION_EXISTS.title}>
						{CRITERION_EXISTS.body}
					</InfoBanner>
				)}
				{outcome?.kind === "unknown" && (
					<InfoBanner variant="warning" title={CRITERION_UNKNOWN.title}>
						{CRITERION_UNKNOWN.body}
					</InfoBanner>
				)}
				{outcome?.kind === "refused" && (
					<InfoBanner variant="danger" title="Nothing was added.">
						{outcome.error}
					</InfoBanner>
				)}

				<Table<TargetingCriterion>
					columns={[
						{
							id: "what",
							header: "Criterion",
							accessor: (c) => <span className="text-xs text-content">{criterionLabel(c)}</span>,
						},
						{
							id: "id",
							header: "",
							accessor: (c) => <span className="font-mono text-xs text-muted">{c.id}</span>,
						},
					]}
					data={criteria ?? []}
					keyExtractor={(c) => c.id}
					loading={criteria === null && failed === null}
					emptyMessage={criteriaEmptyText(failed)}
				/>

				<p className="text-sm text-muted">{DELETE_IS_ON_THE_CARD}</p>
			</Panel>

			{writable && (
				<Panel title={ADD_CRITERION_PANEL}>
					<form className="flex flex-wrap items-end gap-3" onSubmit={search}>
						<div className="max-w-xs grow">
							<Input
								label="Add a place"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder="Search X's places"
							/>
						</div>
						<Button type="submit" variant="outline" size="sm" loading={busy}>
							Search
						</Button>
					</form>
					<p className="text-sm text-muted">{KINDS_ADDED}</p>
					<p className="text-sm text-muted">{PLACE_IN_OR_OUT}</p>
					{searchFailed && (
						<InfoBanner variant="danger" title="X's places could not be read.">
							{searchFailed}
						</InfoBanner>
					)}
					{places !== null && (
						<ul className="grid gap-2">
							{places.length === 0 && <li className="text-sm text-muted">X matched no place.</li>}
							{places.map((place) => (
								<li key={place.targetingValue} className="flex items-center gap-3">
									<span className="text-xs text-content">{locationLabel(place)}</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void add("LOCATION", place.targetingValue)}
									>
										Target this place
									</Button>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void add("LOCATION", place.targetingValue, true)}
									>
										Exclude this place
									</Button>
								</li>
							))}
						</ul>
					)}

					<form className="flex flex-wrap items-end gap-3" onSubmit={searchLanguages}>
						<div className="max-w-xs grow">
							<Input
								label="Add a language"
								value={langQuery}
								onChange={(e) => setLangQuery(e.target.value)}
								placeholder="Search X's languages"
							/>
						</div>
						<Button type="submit" variant="outline" size="sm" loading={busy}>
							Search
						</Button>
					</form>
					{langFailed && (
						<InfoBanner variant="danger" title="X's languages could not be read.">
							{langFailed}
						</InfoBanner>
					)}
					{languages !== null && (
						<ul className="grid gap-2">
							{languages.length === 0 && (
								<li className="text-sm text-muted">X matched no language.</li>
							)}
							{languages.map((l) => (
								<li key={l.targetingValue} className="flex items-center gap-3">
									<span className="text-xs text-content">{languageLabel(l)}</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void add("LANGUAGE", l.targetingValue)}
									>
										Target this language
									</Button>
								</li>
							))}
						</ul>
					)}

					<div className="flex flex-wrap items-end gap-3">
						<div className="max-w-xs grow">
							<Select
								label="Add a gender"
								value={gender}
								onChange={(e) => setGender(e.target.value)}
								placeholder="Choose"
								options={GENDER_OPTIONS.map((g) => ({ ...g }))}
							/>
						</div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={busy || gender === ""}
							onClick={() => void add("GENDER", gender)}
						>
							Target this gender
						</Button>
					</div>

					<div className="flex flex-wrap items-end gap-3">
						<div className="max-w-xs grow">
							<Select
								label="Add an age bucket"
								value={age}
								onChange={(e) => setAge(e.target.value)}
								placeholder="Choose"
								disabled={ageClosed}
								options={AGE_BUCKETS.map((a) => ({ value: a, label: ageLabel(a) }))}
							/>
						</div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							disabled={busy || age === "" || ageClosed}
							onClick={() => void add("AGE", age)}
						>
							Target this age bucket
						</Button>
					</div>
					{ageClosed && <p className="text-sm text-muted">{AGE_ALREADY}</p>}
					<p className="text-sm text-muted">{AGE_ONLY_SEEN}</p>

					<div className="flex flex-wrap items-center gap-3">
						<Button
							type="button"
							variant="outline"
							size="sm"
							loading={busy}
							onClick={() => void readPlatforms()}
						>
							Read X's operating systems
						</Button>
					</div>
					{platformsFailed && (
						<InfoBanner variant="danger" title="X's operating systems could not be read.">
							{platformsFailed}
						</InfoBanner>
					)}
					{platforms !== null && (
						<ul className="grid gap-2">
							{platforms.length === 0 && (
								<li className="text-sm text-muted">X lists no operating system.</li>
							)}
							{platforms.map((p) => (
								<li key={p.targetingValue} className="flex items-center gap-3">
									<span className="text-xs text-content">{p.name}</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void add("PLATFORM", p.targetingValue)}
									>
										Target this operating system
									</Button>
								</li>
							))}
						</ul>
					)}

					<form className="flex flex-wrap items-end gap-3" onSubmit={searchDevices}>
						<div className="max-w-xs grow">
							<Input
								label="Add a device model"
								value={deviceQuery}
								onChange={(e) => setDeviceQuery(e.target.value)}
								placeholder="Search X's device models"
							/>
						</div>
						<Button type="submit" variant="outline" size="sm" loading={busy}>
							Search
						</Button>
					</form>
					{devicesFailed && (
						<InfoBanner variant="danger" title="X's device models could not be read.">
							{devicesFailed}
						</InfoBanner>
					)}
					{devices !== null && (
						<ul className="grid gap-2">
							{devices.length === 0 && (
								<li className="text-sm text-muted">X matched no device model.</li>
							)}
							{devices.map((d) => (
								<li key={d.targetingValue} className="flex items-center gap-3">
									<span className="text-xs text-content">{deviceLabel(d)}</span>
									<Button
										type="button"
										variant="ghost"
										size="sm"
										disabled={busy}
										onClick={() => void add("DEVICE", d.targetingValue)}
									>
										Target this device model
									</Button>
								</li>
							))}
						</ul>
					)}
					<p className="text-sm text-muted">{PLATFORM_DEVICE_OVERLAP}</p>
				</Panel>
			)}
		</>
	);
}
